import { Controller } from "@hotwired/stimulus"
import { createControlChannelSubscription } from "../channels/control_channel"

/**
 * Stimulus controller for live reporting real-time updates.
 * Handles ControlChannel subscription for survey alert events and AcuText area highlighting.
 *
 * Uses the singleton ActionCable consumer via createControlChannelSubscription helper,
 * ensuring proper lifecycle management (automatic cleanup on disconnect).
 *
 * @class LiveReportingController
 * @extends Controller
 */
export default class extends Controller {
  static values = {
    surveyId: Number
  }

  /**
   * Called when the controller is connected to the DOM.
   * Sets up the ControlChannel subscription and AcuText handlers.
   */
  connect() {
    this.setupControlChannel()
    this.setupAcuTextHandlers()
  }

  /**
   * Called when the controller is disconnected from the DOM.
   * Cleans up the ControlChannel subscription and event handlers.
   */
  disconnect() {
    if (this.controlSub) {
      try { this.controlSub.unsubscribe() } catch (_) {}
      this.controlSub = null
    }
    // Remove delegated event handler
    if (this.acuTextHandler) {
      document.removeEventListener("mousedown", this.acuTextHandler)
      this.acuTextHandler = null
    }
  }

  /**
   * Set up the ControlChannel subscription with callbacks for alert events.
   */
  setupControlChannel() {
    if (!this.surveyIdValue) {
      console.warn("LiveReporting: No survey ID provided")
      return
    }

    this.controlSub = createControlChannelSubscription(
      this.surveyIdValue,
      null, // userId not needed for live reporting view
      {
        onAlert: (message, kind) => this.handleAlert(message, kind)
      }
    )
  }

  /**
   * Handle alert_and_end_survey events from ControlChannel.
   * Shows the alert modal with appropriate message.
   *
   * @param {string} message - The alert message
   * @param {string} kind - The alert type (alert_only, alert_and_go_to_question, alert_and_end_survey)
   */
  handleAlert(message, kind) {
    console.log(`LiveReporting: ${kind} received`)

    if (kind === "alert_and_end_survey") {
      this.openAlert(
        message,
        "If you need help or believe an error has occurred, please contact an ACUPOLL Admin"
      )
    }
  }

  /**
   * Show the alert modal with message and callback.
   *
   * @param {string} message - The main alert message
   * @param {string} subMessage - The secondary message
   */
  openAlert(message, subMessage) {
    const $ = window.jQuery
    if (!$) return

    $("#alert-modal-sub-message").text(subMessage)
    $("#alert-button-action").off("click").on("click", () => this.closeAlert())
    $("#alert").toggleClass("d-none", false)
  }

  /**
   * Hide the alert modal.
   */
  closeAlert() {
    const $ = window.jQuery
    if (!$) return

    $("#alert-modal-sub-message").text("")
    $("#alert").toggleClass("d-none", true)
  }

  /**
   * Set up AcuText area click handlers for toggling checkboxes and highlighting.
   * Uses event delegation to survive Turbo frame replacements.
   */
  setupAcuTextHandlers() {
    const $ = window.jQuery
    if (!$) return

    // Use event delegation on document to handle dynamically loaded .area elements
    // This survives Turbo frame replacements
    this.acuTextHandler = (e) => {
      const area = e.target.closest(".area")
      if (!area) return

      const areaIndex = area.getAttribute("area_index")
      if (!areaIndex) return

      $(`.checkBox[data-group="${areaIndex}"]`).click()
      this.refreshAcuText()
    }

    document.addEventListener("mousedown", this.acuTextHandler)

    // Initial refresh to set correct state
    this.refreshAcuText()
  }

  /**
   * Refresh AcuText area highlighting based on checkbox state.
   */
  refreshAcuText() {
    const $ = window.jQuery
    if (!$) return

    $('.area').each(function() {
      const areaIndex = $(this).attr("area_index")
      const selected = $(`.checkBox[data-group="${areaIndex}"]`).attr('value')

      if (selected === 'true') {
        $(this).addClass("area_HL")
      } else {
        $(this).removeClass("area_HL")
      }
    })
  }
}
